/**
 * <gc-hq-stock data-variant-id="..." data-total="..."> splits a variant's stock into two lines:
 *   - online:  total - GC HQ, "N in stock, ready to ship"
 *   - GC HQ:   the in-store pickup quantity, "N in stock at GC HQ" (shown even when 0)
 * Liquid only exposes the all-locations total, so the GC HQ number comes from the backoffice API.
 * Markup: snippets/product-inventory-notice.liquid
 *
 * The product page swaps this snippet's HTML on every variant change, so a custom element is used:
 * connectedCallback runs again for each new copy, unlike an inline <script>.
 */
if (!customElements.get('gc-hq-stock')) {
  const ENDPOINT = 'https://backoffice.brightdigital.co/api/get_inventory_by_location.php';
  const TIMEOUT_MS = 8000;
  const cache = new Map(); // variantId -> Promise<number>
  const STORAGE_PREFIX = "gc-hq-stock-";

  // open the connection to the API now instead of when the first request is made
  const preconnect = document.createElement("link");
  preconnect.rel = "preconnect";
  preconnect.href = new URL(ENDPOINT).origin;
  document.head.appendChild(preconnect);

  // last known number for a variant: lets the line appear instantly, then get corrected by the fresh answer
  const readStored = (variantId) => {
    try {
      const value = sessionStorage.getItem(STORAGE_PREFIX + variantId);
      return value === null ? null : parseInt(value, 10);
    } catch (e) { return null; }
  };
  const writeStored = (variantId, quantity) => {
    try { sessionStorage.setItem(STORAGE_PREFIX + variantId, String(quantity)); } catch (e) { /* storage unavailable */ }
  };

  // The API answers {"available_quantity": "..."} in more than one wording, e.g. "Available: 1" or
  // "0 on hand, Order for pickup"; the count is the first number in the text.
  const fetchQuantity = (variantId) => {
    if (!cache.has(variantId)) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
      const request = fetch(`${ENDPOINT}?variant_id=${encodeURIComponent(variantId)}`, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => {
          // the API has no inventory level when the variant isn't stocked at GC HQ at all: that is 0
          if (data.error && /no inventory data/i.test(data.error)) return 0;
          const match = String(data.available_quantity).match(/\d+/);
          if (!match) throw new Error('no quantity in response');
          return parseInt(match[0], 10);
        })
        .finally(() => clearTimeout(timer));
      request.catch(() => cache.delete(variantId)); // let a later variant change retry
      cache.set(variantId, request);
    }
    return cache.get(variantId);
  };

  // assets/pickup-availability.js can refetch and fully replace the pickup block's markup after
  // this element has already rewritten it (its fetch is usually faster) - reapply when that
  // happens, using whichever variant's numbers were shown last.
  let lastPickupMessage = null;
  document.addEventListener('pickup-availability:rendered', () => {
    if (lastPickupMessage) applyPickupMessage(lastPickupMessage.hqQuantity, lastPickupMessage.total);
  });

  function applyPickupMessage(hqQuantity, total) {
    lastPickupMessage = { hqQuantity, total };
    const pickup = document.querySelector("pickup-availability");
    if (!pickup) return;

    let message = null;
    if (hqQuantity > 0) {
      message = ["Pickup available today at <strong>GC HQ</strong>", "Order online or in-store"];
    } else if (total > 0) {
      message = ["Available for <strong>delivery</strong> or ship to store.", "Usually ready in 2-4 days"];
    }

    pickup.style.display = message ? "" : "none";
    if (!message) return;

    const container = pickup.querySelector(".pickup-availability-information-container");
    const title = pickup.querySelector(".pickup-availability-information__title");
    let stock = pickup.querySelector(".pickup-availability-information__stock");
    if (!title || !container) return;
    if (!stock) {
      stock = document.createElement("p");
      stock.className = "pickup-availability-information__stock";
      title.after(stock);
    }
    title.innerHTML = message[0];
    stock.textContent = message[1];
  }

  class GcHqStock extends HTMLElement {
    connectedCallback() {
      const variantId = this.dataset.variantId;
      if (!variantId) return;

      const stored = readStored(variantId);
      if (stored !== null) this.render(stored);

      fetchQuantity(variantId)
        .then((hqQuantity) => {
          if (!this.isConnected) return; // variant changed while waiting
          writeStored(variantId, hqQuantity);
          this.render(hqQuantity);
        })
        .catch(() => {
          if (!this.isConnected || stored !== null) return; // keep the remembered number if we have one
          this.renderOnline(this.total); // GC HQ number unknown: show the total line like before
        });
    }

    get total() {
      return Number(this.dataset.total) || 0;
    }

    render(hqQuantity) {
      this.renderOnline(Math.max(this.total - hqQuantity, 0));
      this.renderHq(hqQuantity);
      this.syncPickupBlock(hqQuantity);
    }

    // Shopify's pickup block is rewritten from the GC HQ number: in stock there, available online only, or hidden.
    syncPickupBlock(hqQuantity) {
      applyPickupMessage(hqQuantity, this.total);
    }

    show(selector, quantity, template) {
      const notice = this.querySelector(selector);
      notice.querySelector('[data-gc-text]').textContent = template.replace('__QTY__', quantity);
      notice.hidden = false;
      this.hidden = false;
      return notice;
    }

    renderOnline(quantity) {
      if (quantity <= 0) { // nothing to ship online: no line
        this.querySelector("[data-gc-online]").hidden = true;
        return;
      }

      const threshold = Number(this.dataset.threshold) || 0;
      const base = Number(this.dataset.base) || 1;
      const isLow = quantity <= threshold;
      const notice = this.show('[data-gc-online]', quantity, isLow ? this.dataset.textLow : this.dataset.textNormal);

      notice.classList.toggle('product-inventory-notice--low', isLow);
      notice.classList.toggle('product-inventory-notice--normal', !isLow);

      const bar = notice.querySelector('.product-inventory-notice--bar');
      if (bar) bar.style.setProperty('--inventory-scale', Math.min(Math.max(quantity / base, 0), 1));
    }

    renderHq(quantity) {
      this.show('[data-gc-hq]', quantity, this.dataset.textHq);
    }
  }

  customElements.define('gc-hq-stock', GcHqStock);
}